import {Component, EventEmitter, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Product} from '../../../models/product';
import {RouterModule} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {GlobalLoaderService} from '../../../services/global-loader.service';
import {EnvironmentService} from '../../../services/environment.service';
import {HttpClient} from '@angular/common/http';
import {ApiAgentService} from '../../../services/api-agent.service';
import {firstValueFrom} from 'rxjs';
import {getEditorDefaults, PinturaDefaultImageReaderResult} from '@pqina/pintura';
import {CommonModule} from '@angular/common';
import {AngularPinturaModule} from '@pqina/angular-pintura';

declare var Snackbar: any;
declare var FileUploadWithPreview: any;

@Component({
  selector: 'app-product-widget',
  templateUrl: './product-widget.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, AngularPinturaModule, RouterModule],
  styleUrl: './product-widget.component.scss'
})
export class ProductWidgetComponent {
  loadingMainPictures = {value: false};
  serverProductMainImage = {value: null};
  productMainImageUploader = {value: null};
  productMainUrl = {value: null};
  productMainPinturaOptions: any;
  productMainImageFile: any;

  loadingGridPictures = {value: false};
  serverProductGridImage = {value: null};
  productGridImageUploader = {value: null};
  productGridUrl = {value: null};
  productGridPinturaOptions: any;
  productGridImageFile: any;

  loadingThumbnailPictures = {value: false};
  serverProductThumbnailImage = {value: null};
  productThumbnailImageUploader = {value: null};
  productThumbnailUrl = {value: null};
  productThumbnailPinturaOptions: any;
  productThumbnailImageFile: any;

  productForm: FormGroup;
  submitted: boolean;
  productFromServer: Product;

  @ViewChild('mainProductEditor') mainProductEditor?: any;
  @ViewChild('thumbnailProductEditor') thumbnailProductEditor?: any;
  @ViewChild('gridProductEditor') gridProductEditor?: any;

  @Output('product') productEmit = new EventEmitter<any>();
  @Output('close') closeEmit = new EventEmitter<any>();

  constructor(private translate: TranslateService,
              private loader: GlobalLoaderService,
              private environmentService: EnvironmentService,
              private http: HttpClient,
              private formBuilder: FormBuilder,
              private api: ApiAgentService) {
  }

  ngOnInit() {
    this.productForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      price: ['', [Validators.required, Validators.pattern(/-?\d*\.?\d{1,2}/)]],
      qty: ['', [Validators.required, Validators.pattern(/-?\d*\.?\d{1,2}/)]],
      description: [''],
      digital: [''],
      imageBase64: [''],
    });
    this.loadResources().then();
  }

  async imagesAdded(e: any) {
    console.log('images added', e);
    let file = e.detail.cachedFileArray.length > 0 ? e.detail.cachedFileArray[0] : null;
    let image64 = await this.fileToBase64(file);
    if (file == null) {
      return;
    }

    // if the upload from the three images inputs, check the size of the image 2MB.
    if (file.size > 2097152) {
      let quality = 2097152 / file.size;
      this.productThumbnailPinturaOptions = this.buildPqinaEditorOptions('thumbnail', 300, 300, quality);
      this.productMainPinturaOptions = this.buildPqinaEditorOptions('main', 1600, 900, quality);
      this.productGridPinturaOptions = this.buildPqinaEditorOptions('grid', 1200, 1200, quality);
    }
    this.productGridUrl.value = image64;
    this.productThumbnailUrl.value = image64;
    this.productMainUrl.value = image64;
  }

  private fileToBase64(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  async loadResources() {
    // load external javascript libraries.
    await this.loader.loadExternalStyles('/plugins/file-upload/file-upload-with-preview.min.css');
    await this.loader.loadExternalStyles('/plugins/notification/snackbar/snackbar.min.css');
    await this.loader.loadExternalScript('/plugins/notification/snackbar/snackbar.min.js');
    await this.loader.loadExternalScript('/plugins/file-upload/file-upload-with-preview.min.js');
    await this.loader.loadExternalScript('https://cdn.jsdelivr.net/npm/flatpickr');
    await this.loader.loadExternalStyles('https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css');

    this.productGridPinturaOptions = this.buildPqinaEditorOptions('grid', 1200, 1200);
    this.productThumbnailPinturaOptions = this.buildPqinaEditorOptions('thumbnail', 300, 300);
    this.productMainPinturaOptions = this.buildPqinaEditorOptions('main', 1600, 900);

    // load the main picture from server size.
    // if (this.productFromServer && this.productFromServer.productImageId) {
    //   this.loadingMainPictures = true;
    //   this.api.loadProductImage(this.productFromServer.id).then(async value => {
    //     this.serverProductMainImage = value;
    //     this.productMainUrl = this.serverProductMainImage ? 'data:image/png;base64,' + this.serverProductMainImage : this.environmentService.getRest() + '/openApi/downloadProductImage/' + this.productFromServer.id;
    //     this.loadingMainPictures = false;
    //     this.productMainImageUploader = await this.loadImageUploaderWithImage('mainProductImage', this.productMainUrl);
    //   });
    // } else {
    //   this.productMainUrl = null;
    //   this.productMainImageUploader = await this.loadImageUploaderWithImage('mainProductImage', './assets/img/no-image.png');
    // }

    await this.loadPictureFromServer('main', this.loadingMainPictures,
      this.serverProductMainImage,
      this.productMainUrl,
      this.productMainImageUploader,
      this.productFromServer.productImageId,
      this.api.loadProductImage);

    // load grid picture
    // if (this.productFromServer && this.productFromServer.gridImageId) {
    //   this.loadingGridPictures = true;
    //   this.api.loadProductGridImage(this.productFromServer.id).then(async value => {
    //     this.serverProductGridImage = value;
    //     this.productGridUrl = this.serverProductGridImage ? 'data:image/png;base64,' + this.serverProductGridImage : this.environmentService.getRest() + '/openApi/loadProductGridImage/' + this.productFromServer.gridImageId;
    //     this.loadingGridPictures = false;
    //     this.productGridImageUploader = await this.loadImageUploaderWithImage('gridProductImage', this.productGridUrl);
    //   });
    // } else {
    //   this.productGridUrl = null;
    //   this.productGridImageUploader = await this.loadImageUploaderWithImage('gridProductImage', './assets/img/no-image.png');
    // }

    await this.loadPictureFromServer('grid', this.loadingGridPictures,
      this.serverProductGridImage,
      this.productGridUrl,
      this.productGridImageUploader,
      this.productFromServer.productImageId,
      this.api.loadProductGridImage);

    // load thumbnail picture from the server
    // if (this.productFromServer && this.productFromServer.thumbnailImageId) {
    //   this.loadingThumbnailPictures = true;
    //   this.api.loadProductThumbnailImage(this.productFromServer.id).then(async value => {
    //     this.serverProductThumbnailImage = value;
    //     this.loadingThumbnailPictures = false;
    //     this.productThumbnailUrl = this.serverProductThumbnailImage ? 'data:image/png;base64,' + this.serverProductThumbnailImage : this.environmentService.getRest() + '/openApi/downloadProductThumbnailImageBytes/' + this.productFromServer.id;
    //     this.productThumbnailImageUploader = await this.loadImageUploaderWithImage('thumbnailProductImage', this.productThumbnailUrl);
    //   });
    // } else {
    //   this.productThumbnailUrl = null;
    //   this.productThumbnailImageUploader = await this.loadImageUploaderWithImage('thumbnailProductImage', './assets/img/no-image.png');
    // }

    await this.loadPictureFromServer('thumbnail', this.loadingThumbnailPictures,
      this.serverProductThumbnailImage,
      this.productThumbnailUrl,
      this.productThumbnailImageUploader,
      this.productFromServer.productImageId,
      this.api.loadProductThumbnailImage);
  }

  private async loadPictureFromServer(imageType: any,
                                      loadingPicturesRef: { value: boolean },
                                      serverProductImageRef: { value: any },
                                      productUrlRef: { value: string },
                                      productImageUploaderRef: { value: any },
                                      productFromServerId: any,
                                      apiFunction: any) {
    if (this.productFromServer && this.productFromServer.productImageId) {
      loadingPicturesRef.value = true;
      apiFunction(this.productFromServer.id).then(async (value: any) => {
        serverProductImageRef.value = value;
        loadingPicturesRef.value = false;
        productUrlRef.value = serverProductImageRef.value ? 'data:image/png;base64,' + serverProductImageRef.value : this.environmentService.getRest() + '/openApi/downloadProduct' + imageType + 'ImageBytes/' + productFromServerId;
        productImageUploaderRef.value = await this.loadImageUploaderWithImage(imageType + 'ProductImage', productUrlRef);
      });
    } else {
      productUrlRef.value = null;
      productImageUploaderRef.value = await this.loadImageUploaderWithImage(imageType + 'ProductImage', {value: './assets/img/no-image.png'});
    }
  }

  // @ts-ignore
  get f() {
    return this.productForm.controls;
  }

  showToast(msg: any) {
    Snackbar.show({
      text: msg,
      pos: 'bottom-center'
    });
  }

  async sendToParent(image: any, thumbnailImage: any, gridImage: any) {
    let price = this.productForm.value['price'];
    let title = this.productForm.value['title'];
    let qty = this.productForm.value['qty'];
    let desc = this.productForm.value['description'];
    try {
      this.productEmit.emit({
        title: title,
        price: price,
        productImageId: image,
        gridImageId: gridImage,
        thumbnailImageId: thumbnailImage,
        description: desc,
        stockQty: qty,
        isDigital: false,

        id: -1,
        qty: qty,

      });

    } catch (e) {
      console.log('error', e);
      this.showToast(await firstValueFrom(this.translate.get('Could not save the product')));
    } finally {
    }
  }

  private readFileFromAsset(assetName: any) {
    return new Promise(resolve => {
      this.http.get('./assets/img/' + assetName, {responseType: 'blob'}).subscribe((res => {
            const reader = new FileReader();
            reader.onloadend = () => {
              let base64data = reader.result;
              resolve(base64data);
            };
            reader.readAsDataURL(res);
          }
        )
      );
    });
  }

  async closeModal() {
    this.closeEmit.emit();
  }

  async submitForm() {
    this.submitted = true;
    if (this.productThumbnailImageFile == null && this.thumbnailProductEditor != null) {
      console.log('product thumbnail image file', this.productThumbnailImageFile);
      await this.thumbnailProductEditor.editor.processImage();
      console.log('product thumbnail image file', this.productThumbnailImageFile);
    }

    if (this.productGridImageFile == null && this.gridProductEditor != null) {
      console.log('product grid image file', this.productGridImageFile);
      await this.gridProductEditor.editor.processImage();
      console.log('product grid image file', this.productGridImageFile);
    }

    if (this.productMainImageFile == null && this.mainProductEditor != null) {
      console.log('product main image file', this.productMainImageFile);
      await this.mainProductEditor.editor.processImage();
      console.log('product main image file', this.productMainImageFile);
    }

    if (this.productForm.valid) {
      let thumbnailImage64 = null;
      let mainImage64 = null;
      let gridImage64 = null;
      if (this.productThumbnailImageFile) {
        thumbnailImage64 = await this.fileToBase64(this.productThumbnailImageFile);
      }

      if (this.productGridImageFile) {
        gridImage64 = await this.fileToBase64(this.productGridImageFile);
      }

      if (this.productMainImageFile) {
        mainImage64 = await this.fileToBase64(this.productMainImageFile);
      }

      if (!mainImage64 && !this.serverProductMainImage.value) {
        console.log('no main image');
        mainImage64 = await this.readFileFromAsset('no-image-1600x1200.jpg');
      }

      if (!thumbnailImage64 && !this.serverProductThumbnailImage.value) {
        thumbnailImage64 = await this.readFileFromAsset('no-image-300x300.jpg');
      }

      if (!gridImage64 && !this.serverProductGridImage.value) {
        gridImage64 = await this.readFileFromAsset('no-image-1200x1200.jpg');
      }

      this.sendToParent(mainImage64, thumbnailImage64, gridImage64).then();

    } else {
      console.log('errors', this.f);
    }
  }

  buildPqinaEditorOptions(productImageState: any, width: any, height: any, quality = 1): any {
    return getEditorDefaults({
      imageWriter: {
        quality: quality,
        targetSize: {
          width: width,
          height: height,
          fit: 'contain',
          upscale: false,
        },
        preprocessImageState: (imageState: any) => {
          // create a new annotation array
          imageState.annotation = imageState.annotation.map((shape: any) => {
            // this is not a text shape so skip
            if (!shape.text) {
              return shape;
            }
            // replace placeholders in text properties
            shape.text = shape.text.replace(/{name}/g, 'Paylink');
            return shape;
          });

          // return updated image state
          return imageState;
        },
        store: (state: any, options: any, onprogress: any) => {
          return new Promise((resolve) => {
            switch (productImageState) {
              case 'main':
                this.productMainImageFile = state.dest;
                break;
              case 'thumbnail':
                this.productThumbnailImageFile = state.dest;
                break;
              case 'grid':
                this.productGridImageFile = state.dest;
                break;
            }
            console.log('options', options);
            console.log('onprogress', onprogress);
            console.log('state', state);
            resolve(state);
          });
        }
      }
    });
  }

  private async loadImageUploaderWithImage(imageUploaderId: any, imageUrlRef = {value: null}) {
    console.log('load image uploader with image');
    let chooseFileLbl = await firstValueFrom(this.translate.get('Choose product image'));
    let browseLbl = await firstValueFrom(this.translate.get('Browse'));
    let imageUploader: any;
    imageUploader = new FileUploadWithPreview(imageUploaderId, {
      text: {
        chooseFile: chooseFileLbl,
        browse: browseLbl
      },
      images: {
        baseImage: imageUrlRef.value
      }
    });
    return imageUploader;
  }

  async imageLoaded($event: PinturaDefaultImageReaderResult) {
    this.mainProductEditor.editor.imageCropAspectRatio = 16 / 9;
    this.thumbnailProductEditor.editor.imageCropAspectRatio = 1;
    this.gridProductEditor.editor.imageCropAspectRatio = 1;
    console.log('image loaded', $event);
    // window.addEventListener('fileUploadWithPreview:imagesAdded', (e) => this.imagesAdded(e));
    await this.imagesAdded($event.dest);
  }
}
